import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cn } from '../cn';

// Canonical content box for the modern skin.
//
// Replaces ~184 hand-rolled `rounded-sm/md border border-border bg-panel`
// boxes that had drifted into two corner radii and inconsistent padding. One
// component, one radius (rounded-sm per DESIGN.md "cards are not pillows"), one
// padding scale. Every bordered surface on a game page should be a <Panel>.
//
// `flush` drops the internal padding for list/table panels that manage their
// own row padding via dividers (e.g. the jail roster, mail thread list).
//
// Polymorphic via `as` prop. Element-specific props (`onSubmit`, `action`,
// `method`, etc.) are typed concretely against the rendered element type, so
// `<Panel as="form" onSubmit={...}>` resolves `onSubmit` against
// `HTMLFormElement` with no casts needed. Pattern: generic `E extends
// ElementType`, spread `ComponentPropsWithoutRef<E>` minus our own keys.

type PanelOwnProps<E extends ElementType> = {
  children: ReactNode;
  /** Drop the default inner padding (for divider-separated list/table panels). */
  flush?: boolean;
  /** Element to render as. Defaults to <section>. */
  as?: E;
  className?: string;
  /**
   * Opt-in: remove the default `overflow-hidden` so that absolutely-positioned
   * children (e.g. autocomplete dropdowns) can overflow the panel boundary.
   * Defaults to false — no existing Panel changes appearance.
   */
  allowOverflow?: boolean;
};

type PanelProps<E extends ElementType = 'section'> = PanelOwnProps<E> &
  Omit<ComponentPropsWithoutRef<E>, keyof PanelOwnProps<E>>;

export function Panel<E extends ElementType = 'section'>({
  children,
  flush = false,
  allowOverflow = false,
  as,
  className,
  ...rest
}: PanelProps<E>) {
  const As = (as ?? 'section') as ElementType;
  return (
    <As
      className={cn(
        'rounded-sm border border-border bg-panel',
        !allowOverflow && 'overflow-hidden',
        !flush && 'p-4 sm:p-6',
        className,
      )}
      {...rest}
    >
      {children}
    </As>
  );
}

// Optional panel header strip for flush panels (e.g. "Jail: 0 inmates").
// Matches the divider-separated header the roster/list panels already use.
export function PanelHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'border-b border-divider px-4 py-3 text-[12px] font-medium uppercase leading-[1.3] tracking-[0.05em] text-ink-high sm:px-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
